import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { Journey, JourneyMission, Quest, WaitlistItem } from "@/types/quests"

// Tipo para el mapa de intentos de misiones
type MissionAttemptsMap = {
  [missionId: string]: string // missionId -> attemptId
}

interface QuestsStore {
  // State
  availableQuests: Quest[]
  upcomingQuests: Quest[]
  journeys: Journey[]
  waitlists: WaitlistItem[]
  currentMission: JourneyMission | null
  currentAttemptId: string | null
  currentJourney: Journey | null
  missionAttempts: MissionAttemptsMap // Map to store attempts
  loading: boolean
  startingJourney: boolean
  loadingQuest: boolean
  currentJourneyId: string | null

  // Actions for Quests
  setAvailableQuests: (availableQuests: Quest[]) => void
  setUpcomingQuests: (upcomingQuests: Quest[]) => void
  setJourneys: (journeys: Journey[]) => void
  setWaitlists: (waitlists: WaitlistItem[]) => void
  setCurrentJourney: (currentJourney: Journey | null) => void
  getJourneyById: (id: string) => Journey | undefined
  setCurrentJourneyId: (journeyId: string) => void

  // Acciones para misiones
  setCurrentMission: (mission: JourneyMission | null) => void
  setCurrentAttemptId: (attemptId: string | null) => void

  // Acciones para intentos de misiones
  addMissionAttempt: (missionId: string, attemptId: string) => void
  removeMissionAttempt: (missionId: string) => void
  clearMissionAttempts: () => void

  // Acciones para estados de carga
  setLoading: (loading: boolean) => void
  setStartingJourney: (startingJourney: boolean) => void
  setLoadingQuest: (loading: boolean) => void
}

// Create a store with both persist and devtools middleware
export const useQuestsStore = create<QuestsStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        availableQuests: [],
        upcomingQuests: [],
        journeys: [],
        waitlists: [],
        currentMission: null,
        currentAttemptId: null,
        currentJourney: null,
        missionAttempts: {},
        loading: false,
        startingJourney: false,
        loadingQuest: false,
        currentJourneyId: null,

        setAvailableQuests: (availableQuests: Quest[]) => {
          set({ availableQuests })
        },
        setUpcomingQuests: (upcomingQuests: Quest[]) => {
          set({ upcomingQuests })
        },
        setJourneys: (journeys: Journey[]) => {
          set({ journeys })
        },
        setWaitlists: (waitlists: WaitlistItem[]) => {
          set({ waitlists })
        },
        setCurrentJourney: (currentJourney: Journey | null) => {
          set({ currentJourney })
        },
        getJourneyById: (id: string) => {
          return get().journeys.find((journey: Journey) => journey.id === id)
        },
        setCurrentJourneyId: (journeyId: string) => {
          set({ currentJourneyId: journeyId })
        },

        // Actions for missions
        setCurrentMission: (mission: JourneyMission | null) => {
          set({ currentMission: mission })
        },
        setCurrentAttemptId: (attemptId: string | null) => {
          set({ currentAttemptId: attemptId })
        },

        // Actions for mission attempts
        addMissionAttempt: (missionId: string, attemptId: string) => {
          set((state: QuestsStore) => ({
            missionAttempts: {
              ...state.missionAttempts,
              [missionId]: attemptId,
            },
          }))
        },
        removeMissionAttempt: (missionId: string) => {
          set((state: QuestsStore) => {
            const newAttempts = { ...state.missionAttempts }
            delete newAttempts[missionId]
            return { missionAttempts: newAttempts }
          })
        },
        clearMissionAttempts: () => {
          set({ missionAttempts: {} })
        },

        // Loading actions
        setLoading: (loading: boolean) => {
          set({ loading })
        },
        setStartingJourney: (startingJourney: boolean) => {
          set({ startingJourney })
        },
        setLoadingQuest: (loading: boolean) => {
          set({ loadingQuest: loading })
        },
      }),
      {
        name: "pandora-quests-storage",
        partialize: (state: QuestsStore) => ({
          // We only persist between sessions what we must.
          missionAttempts: state.missionAttempts,
        }),
      },
    ),
    { name: "quests-store", enabled: process.env.NODE_ENV !== "production" },
  ),
)
